import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { isNil } from 'lodash';
import { Observable } from 'rxjs';
import { restEndpoint } from 'config';
import { UserService } from 'shared/services';
import { map } from 'rxjs/operators';
import { AvailableLanguage } from 'helpers';
import { ApiModel } from 'app/shared/models/api.model';

@Injectable()
export class LandingPageModel {
    private readonly apiUrl = this.apiModel.getLandingPageEndpoint();
    constructor(
        private apiModel: ApiModel,
        private userService: UserService
    ) {}

    public create({ brandId, layout, published }: CreateLandingPageParameters): Observable<LandingPage> {
        const url = `${this.apiUrl}`;
        return this.apiModel.makeHttpObservableRequest({
            method: 'POST',
            url,
            body: {
                brandId,
                layout,
                published
            }
        });
    }

    public getById(id: string): Observable<LandingPage> {
        const url = `${this.apiUrl}/${id}`;
        return this.apiModel.makeHttpObservableRequest({
            method: 'GET',
            url
        });
    }

    public getAll({
        brandId,
        offset = 0,
        limit = 20,
        published
    }: GetLandingPagesParameters): Observable<GetAllLandingPagesResponse> {
        const url = `${this.apiUrl}`;

        const urlParams = new URLSearchParams({ brandId, offset: offset.toString(), limit: limit.toString() });

        if (!isNil(published)) {
            urlParams.append('published', published ? 'true' : 'false');
        }

        return this.apiModel.makeHttpObservableRequest({
            method: 'GET',
            url: `${url}?${urlParams.toString()}`
        });
    }

    public updateLandingPage(landingPage: LandingPage): Observable<LandingPage> {
        const url = `${this.apiUrl}/${landingPage._id}`;
        return this.apiModel.makeHttpObservableRequest({
            method: 'PUT',
            url,
            body: landingPage
        });
    }

    public deleteLandingPage(landingPage: LandingPage): Observable<LandingPage> {
        const url = `${this.apiUrl}/${landingPage._id}`;
        return this.apiModel.makeHttpObservableRequest({
            method: 'DELETE',
            url
        });
    }

    public getPublishedLandingPage(brandId: string): Observable<LandingPage | null> {
        return this.getAll({ brandId, published: true }).pipe(
            map((response) => {
                const { landingPages } = response;
                return landingPages.length ? landingPages[0] : null;
            })
        );
    }

    public uploadAsset(file: File): Observable<AssetUploadResponse> {
        const url = `${this.apiUrl}/upload-asset`;

        const formData = new FormData();
        formData.append('file', file);

        return this.apiModel.makeHttpObservableRequest({
            method: 'POST',
            url,
            body: formData
        });
    }

    public downloadDocument(documentId: string): Promise<any> {
        return this.apiModel
            .makeHttpObservableBlobRequest({
                method: 'GET',
                url: this.getDocumentDownloadUrlById(documentId),
                headers: {
                    Authorization: `Bearer ${this.userService.getAuthToken()}`
                }
            })
            .toPromise();
    }

    private getDocumentDownloadUrlById(id: string): string {
        return `${restEndpoint}/internal${this.apiUrl}/document/${id}`;
    }
}

export interface GetLandingPagesParameters {
    brandId: string;
    limit?: number;
    offset?: number;
    published?: boolean;
}

export interface GetAllLandingPagesResponse {
    count: number;
    landingPages: LandingPage[];
}

export interface CreateLandingPageParameters {
    brandId: string;
    layout?: Layout[];
    published?: boolean;
}

export enum ComponentType {
    Text = 'text',
    Image = 'image',
    Documents = 'documents',
    Cover = 'cover',
    Catalog = 'catalog'
}

export interface LandingPageComponent {
    _id?: string;
    type: ComponentType;
    sortOrder: number;
    dropIndex?: number;
    config?: Record<string, unknown>;
    params?: {
        image?: LocalizedFileParam;
        documents?: LocalizedFilesParam;
    };
}

export type LocalizedItem<T> = Partial<Record<AvailableLanguage, T>>;
export type LocalizedString = LocalizedItem<string>;
export type LocalizedCmsAsset = LocalizedItem<CmsAsset | null>;
export type LocalizedDocuments = LocalizedItem<LandingPageDocument[]>;
export type LocalizedFileParam = LocalizedItem<File | null>;
export type LocalizedFilesParam = LocalizedItem<File[] | null>;

export interface LandingPageTextComponent extends LandingPageComponent {
    config: {
        text: LocalizedString;
        description: LocalizedString;
        image?: LocalizedCmsAsset;
        redirectUrl?: LocalizedString;
    };
}

export interface LandingPageImageComponent extends LandingPageComponent {
    config: {
        image: LocalizedCmsAsset;
        alt: LocalizedString;
        redirectUrl?: LocalizedString;
    };
}

export interface LandingPageCoverComponent extends LandingPageComponent {
    config: {
        image: LocalizedCmsAsset;
    };
}

export interface LandingPageHeaderComponent extends LandingPageComponent {
    config: {
        image: LocalizedCmsAsset;
    };
}

export interface LandingPageDocumentsComponent extends LandingPageComponent {
    config: {
        documents: LocalizedDocuments;
        title: LocalizedString;
    };
}

export interface LandingPageCatalogComponent extends LandingPageComponent {
    config: {
        catalogId: string;
    };
}

export enum ScreenSize {
    Desktop = 'desktop'
}

export interface Layout {
    components: LandingPageComponent[];
}
export interface LandingPage {
    _id?: string;
    brandId: any;
    draftLayout: Layout;
    publishedLayout?: Layout;
    published: boolean;
    publishedAt?: Date;
}

export interface CmsAsset {
    _id?: string;
    name: string;
    type: string;
    url?: string;
}

export interface AssetUploadResponse {
    fileId: string; // asset_67312323d3c15133e14c30d6
    key: string; // assets/asset_67312323d3c15133e14c30d6.jpg
    size: number;
    extension: string;
}

export interface LandingPageDocument {
    file: CmsAsset;
    redirectUrl?: string;
}

export function createLocalizedItem<T>(value: T): LocalizedItem<T> {
    return Object.values(AvailableLanguage).reduce(
        (acc, key) => {
            acc[key] = Array.isArray(value) ? ([...value] as T) : value;
            return acc;
        },
        {} as unknown as LocalizedItem<T>
    );
}

export function createEmptyLocalizedAsset(): LocalizedCmsAsset {
    return createLocalizedItem<CmsAsset | null>(null);
}

export function createEmptyLocalizedString(): LocalizedString {
    return createLocalizedItem<string>('');
}

export function createEmptyLocalizedDocuments(): LocalizedDocuments {
    return createLocalizedItem<LandingPageDocument[]>([]);
}

export function createMockLandingPage(values?: Partial<LandingPage>): LandingPage {
    return {
        _id: '123',
        brandId: '123',
        draftLayout: values?.draftLayout || {
            components: [
                createMockLandingTextComponent(),
                createMockLandingCoverComponent(),
                createMockLandingImageComponent(),
                createMockLandingDocumentsComponent()
            ]
        },
        publishedLayout: values?.publishedLayout || {
            components: [
                createMockLandingTextComponent(),
                createMockLandingCoverComponent(),
                createMockLandingImageComponent(),
                createMockLandingDocumentsComponent()
            ]
        },
        published: true,
        ...values
    };
}

export function createMockCmsAsset(values?: Partial<CmsAsset>): CmsAsset {
    return {
        _id: '123',
        name: 'name',
        type: 'type',
        url: 'url',
        ...values
    };
}

export function createMockLandingTextComponent(values?: Partial<LandingPageTextComponent>): LandingPageTextComponent {
    return {
        type: ComponentType.Text,
        sortOrder: values?.sortOrder || 1,
        config: values?.config || {
            text: createLocalizedItem('Title mock'),
            description: createLocalizedItem('Content mock')
        }
    };
}

export function createMockLandingCoverComponent(
    values?: Partial<LandingPageCoverComponent>
): LandingPageCoverComponent {
    return {
        type: ComponentType.Cover,
        sortOrder: values?.sortOrder || 1,
        config: values?.config || {
            image: createLocalizedItem<CmsAsset>(createMockCmsAsset())
        }
    };
}

export function createMockLandingImageComponent(
    values?: Partial<LandingPageImageComponent>
): LandingPageImageComponent {
    return {
        type: ComponentType.Image,
        sortOrder: values?.sortOrder || 1,
        config: values?.config || {
            image: createLocalizedItem<CmsAsset>(createMockCmsAsset()),
            alt: createEmptyLocalizedString(),
            redirectUrl: createEmptyLocalizedString()
        }
    };
}

export function createMockLandingDocumentsComponent(
    values?: Partial<LandingPageDocumentsComponent>
): LandingPageDocumentsComponent {
    return {
        type: ComponentType.Documents,
        sortOrder: values?.sortOrder || 1,
        config: values?.config || {
            documents: {
                en: [
                    {
                        file: createMockCmsAsset(),
                        redirectUrl: 'redirectUrl'
                    }
                ]
            },
            title: createLocalizedItem('Title mock')
        }
    };
}

export function createDefaultLayout(): Layout {
    return {
        components: [
            {
                type: ComponentType.Cover,
                sortOrder: 1,
                config: {
                    image: createEmptyLocalizedAsset()
                }
            }
        ]
    };
}

export function createEmptyCoverComponent(): LandingPageCoverComponent {
    return {
        type: ComponentType.Cover,
        sortOrder: 1,
        config: {
            image: createEmptyLocalizedAsset()
        }
    };
}

export function isDefaultLayout(layout: Layout): boolean {
    const defaultLayout = createDefaultLayout();

    return _.isEqual(layout, defaultLayout);
}
